import React, { useEffect, useMemo } from 'react';
import Link from 'next/link';
import { Box, Divider, Typography } from '@mui/material';
import { format, isSameDay } from 'date-fns';
import { useTranslation } from 'i18n';
import { routes } from 'utils/routing';

interface MobileEventsAgendaProps {
  eventsByDate: Map<string, any[]>;
  sourceEvents: any[];
  selectedDate?: Date | null;
  onVisibleDayChange?: (date: Date) => void;
}

const formatDateLabel = (event: any): string => {
  const start = new Date(event.start);
  const end = new Date(event.end);

  const isMultiDay = event.sessions?.length > 1 || !isSameDay(start, end);

  if (isMultiDay) {
    return `${format(start, 'EEE, MMM d')} - ${format(end, 'EEE, MMM d')}`;
  }

  return format(start, 'EEE, MMM d, h:mm a');
};

// Derive org name from source event.
// Primary: event.organization.title (matches EventsDialog pattern).
// Fallback: first organization_mentions entry, then special_hosts[0].name.
const getOrgInfo = (sourceEvent: any): { name: string; additionalCount: number; id: number | null } => {
  if (!sourceEvent) return { name: '', additionalCount: 0, id: null };

  const primaryOrg = sourceEvent.organization?.title ?? null;
  const primaryId = sourceEvent.organization?.id ?? null;
  const mentions: any[] = sourceEvent.organization_mentions ?? [];

  if (primaryOrg) {
    return { name: primaryOrg, additionalCount: mentions.length, id: primaryId };
  }

  const fallbackOrg = mentions[0]?.organization ?? null;
  const fallbackName = fallbackOrg?.title ?? sourceEvent.special_hosts?.[0]?.name ?? '';
  const fallbackId = fallbackOrg?.id ?? null;

  return { name: fallbackName, additionalCount: Math.max(0, mentions.length - 1), id: fallbackId };
};

const MobileEventsAgenda: React.FC<MobileEventsAgendaProps> = ({
  eventsByDate,
  sourceEvents,
  selectedDate,
  onVisibleDayChange,
}) => {
  const [t] = useTranslation('public');

  const sortedDayKeys = useMemo(() => {
    if (!selectedDate) return [];
    const selectedKey = format(selectedDate, 'yyyy-MM-dd');
    return eventsByDate.has(selectedKey) ? [selectedKey] : [];
  }, [eventsByDate, selectedDate]);

  const sourceEventsById = useMemo(() => {
    const map = new Map<number | string, any>();
    sourceEvents.forEach((event) => {
      if (event?.id != null) map.set(event.id, event);
    });
    return map;
  }, [sourceEvents]);

  useEffect(() => {
    if (!onVisibleDayChange || sortedDayKeys.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const dayKey = (entry.target as HTMLElement).dataset.daySection;
            if (dayKey) {
              onVisibleDayChange(new Date(dayKey));
            }
          }
        });
      },
      {
        rootMargin: '-30% 0px -70% 0px',
        threshold: 0,
      },
    );

    const sections = document.querySelectorAll('[data-day-section]');
    sections.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [sortedDayKeys, onVisibleDayChange]);

  if (eventsByDate.size === 0) {
    return (
      <Box px={2} py={4}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No events for this month.
          {/* {t('calendar.no_events_this_month')} */}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {sortedDayKeys.map((dayKey) => {
        const dayEvents = eventsByDate.get(dayKey) ?? [];

        return (
          <Box
            key={dayKey}
            component="section"
            id={`day-${dayKey}`}
            data-day-section={dayKey}
            sx={{ scrollMarginTop: '80px' }}
          >
            {dayEvents.map((calEvent) => {
              const sourceEvent = sourceEventsById.get(calEvent.eventId) ?? null;
              const { name: orgName, additionalCount, id: orgId } = getOrgInfo(sourceEvent);
              const dateLabel = formatDateLabel(calEvent);
              const href = routes.event(calEvent.eventId).link;

              return (
                <Box key={calEvent.id}>
                  <Link href={href} passHref legacyBehavior>
                    <Box
                      component="a"
                      sx={{
                        display: 'block',
                        px: 2,
                        py: 1.5,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { backgroundColor: '#F5F6FA' },
                      }}
                    >
                      <Typography variant="body2" color="text.primary" mb={0.25}>
                        {dateLabel}
                      </Typography>

                      <Typography variant="h6" fontWeight={600} fontSize={15} lineHeight={1.3} mb={0.25}>
                        {calEvent.title}
                      </Typography>

                      {orgName && (
                        <Typography variant="body2" color="#7B8BAD">
                          {t('common:general.hostedBy')}{' '}
                          {orgId ? (
                            <Link href={routes.organizationProfile(orgId).link} passHref legacyBehavior>
                              <Box
                                component="a"
                                sx={{ color: '#4E6DF5', textDecoration: 'none' }}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              >
                                {orgName}
                                {additionalCount > 0 ? ` +${additionalCount} ${t('common:general.other')}` : ''}
                              </Box>
                            </Link>
                          ) : (
                            <Box component="span" sx={{ color: '#4E6DF5' }}>
                              {orgName}
                              {additionalCount > 0 ? ` +${additionalCount} ${t('common:general.other')}` : ''}
                            </Box>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Link>

                  <Divider sx={{ borderColor: '#D2D8EC', mx: 2 }} />
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default MobileEventsAgenda;
